import { useEffect, useState } from "react";

/**
 * Track which section is currently in view based on IntersectionObserver.
 * Watches elements by id; returns the id of the section closest to the top
 * of the viewport that is at least partially visible.
 */
export default function useActiveSection(ids, { enabled = true } = {}) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!enabled || !ids || ids.length === 0) return;
    const visible = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            visible.set(e.target.id, e.intersectionRatio);
          } else {
            visible.delete(e.target.id);
          }
        });
        // Pick the section that has the highest intersection ratio; if tied, first in order
        let best = null; let bestRatio = 0;
        for (const id of ids) {
          const r = visible.get(id);
          if (r != null && r > bestRatio) { bestRatio = r; best = id; }
        }
        setActive(best);
      },
      { rootMargin: "-25% 0px -50% 0px", threshold: [0, 0.15, 0.3, 0.5, 0.75, 1] }
    );

    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [ids, enabled]);

  return active;
}
